import React, { useEffect, useRef, useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import * as THREE from "three";
import MicOffIcon from "@mui/icons-material/MicOff";

let desiredScale = 1.5;

const VoiceVisualizer = ({ visualizerMode, externalAudio, isProcessing = false, isMuted = false }) => {
  const canvasRef = useRef(null);
  const orbContainerRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const streamRef = useRef(null);
  const micSourceRef = useRef(null);
  const externalSourceRef = useRef(null);
  const [audioConnected, setAudioConnected] = useState(false);

  // Initialize audio context once
  useEffect(() => {
    if (!audioContextRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      dataArrayRef.current = dataArray;
    }

    return () => {
      // Cleanup audio context on component unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Handle external audio connection
  useEffect(() => {
    if (!externalAudio || !audioContextRef.current || !analyserRef.current)
      return;

    // Prevent connecting the same audio element multiple times
    if (externalAudio._connected) return;

    try {
      // Resume audio context if it's suspended
      if (audioContextRef.current.state === "suspended") {
        audioContextRef.current.resume();
      }

      const source =
        audioContextRef.current.createMediaElementSource(externalAudio);
      externalSourceRef.current = source;
      source.connect(analyserRef.current);
      source.connect(audioContextRef.current.destination);

      // Mark the audio element as connected
      externalAudio._connected = true;
      setAudioConnected(true);
    } catch (err) {
      console.warn("Error connecting external audio:", err);
    }
  }, [externalAudio]);

  // Bot orb visualization
  useEffect(() => {
    if (visualizerMode !== "bot") return;

    let scene, camera, renderer, orbCore, orbGlow, clock;
    let animFrame;
    let scaleAnimationFrame;
    let isInitialized = false;

    const initThreeOrb = () => {
      if (!orbContainerRef.current) return;

      try {
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(
          75,
          orbContainerRef.current.clientWidth /
            orbContainerRef.current.clientHeight,
          0.1,
          1000
        );
        camera.position.z = 1.5;
        renderer = new THREE.WebGLRenderer({ 
          antialias: true, 
          alpha: true,
          powerPreference: "high-performance" // Optimize for performance
        });
        renderer.setSize(
          orbContainerRef.current.clientWidth,
          orbContainerRef.current.clientHeight
        );
        orbContainerRef.current.innerHTML = ""; // Clear previous renderer
        orbContainerRef.current.appendChild(renderer.domElement);

        const geometry = new THREE.SphereGeometry(1, 64, 64); // Reduced segments for better performance
        clock = new THREE.Clock();

        const coreMat = new THREE.ShaderMaterial({
          uniforms: {
            uTime: { value: 0 },
            uScale: { value: 1 },
            uWaveScale: { value: 0.02 },
          },
          vertexShader: `
          uniform float uTime, uScale, uWaveScale;
          varying vec3 vPos;
          void main() {
            vPos = position;
            float wave = sin(position.x*5. + uTime*3.)*uWaveScale
                       + sin(position.y*6. + uTime*4.)*uWaveScale
                       + sin(position.z*7. + uTime*2.5)*uWaveScale;
            vec3 pos = position * (1.0 + wave * uScale);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(pos,1.);
          }`,
          fragmentShader: `
          uniform float uTime;
          varying vec3 vPos;
          void main() {
            vec3 darkPurple = vec3(0.4, 0.1, 0.6);
            vec3 brightPurple = vec3(0.4, 0.3, 0.9);
            float t = sin(vPos.x*2. + uTime*2.) * 0.5 + 0.5;
            vec3 color = mix(darkPurple, brightPurple, t);
            gl_FragColor = vec4(color, 1.0);
          }
          `,
        });

        orbCore = new THREE.Mesh(geometry, coreMat);
        orbCore.scale.set(0.7, 0.7, 0.7);
        scene.add(orbCore);

        const glowMat = new THREE.ShaderMaterial({
          uniforms: {
            uTime: { value: 0 },
            uScale: { value: 1 },
            uIntensity: { value: 1.2 },
            uWaveScale: { value: 0.02 },
          },
          vertexShader: `
          uniform float uTime, uScale, uWaveScale;
          varying vec3 vPos;
          void main() {
            vPos = position;
            float wave = sin(position.x*5. + uTime*3.)*uWaveScale
                       + sin(position.y*6. + uTime*4.)*uWaveScale
                       + sin(position.z*7. + uTime*2.5)*uWaveScale;
            vec3 pos = position * (1.0 + wave * uScale);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(pos,1.);
          }`,
          fragmentShader: `
          uniform float uTime, uIntensity;
          varying vec3 vPos;
          void main() {
            float rim = pow(1.0 - abs(dot(normalize(vPos), vec3(0,0,1))), 2.5);
            vec3 glowBase = vec3(0.3, 0.2, 0.8);
            vec3 glow = glowBase * rim * uIntensity;
            gl_FragColor = vec4(glow, rim * 0.7);
          }
          `,
          blending: THREE.AdditiveBlending,
          transparent: true,
          depthWrite: false,
        });

        orbGlow = new THREE.Mesh(geometry, glowMat);
        orbGlow.scale.set(0.77, 0.77, 0.77);
        scene.add(orbGlow);

        const pl = new THREE.PointLight(0xffffff, 1.0);
        pl.position.set(0, 0, 2);
        scene.add(pl);

        isInitialized = true;
      } catch (error) {
        console.error("Error initializing Three.js orb:", error);
        // Fallback to simpler visualization if Three.js fails
        if (orbContainerRef.current) {
          orbContainerRef.current.innerHTML = `
            <div style="
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              background: radial-gradient(circle, rgba(126,70,255,0.2) 0%, rgba(126,70,255,0) 70%);
            ">
              <div style="
                width: 200px;
                height: 200px;
                border-radius: 50%;
                background: radial-gradient(circle, rgba(126,70,255,0.8) 0%, rgba(126,70,255,0.4) 100%);
                animation: pulse 2s infinite;
              "></div>
            </div>
            <style>
              @keyframes pulse {
                0% { transform: scale(0.95); opacity: 0.7; }
                50% { transform: scale(1.05); opacity: 1; }
                100% { transform: scale(0.95); opacity: 0.7; }
              }
            </style>
          `;
        }
      }
    };

    const scaleAnimation = () => {
      if (!isInitialized) return;
      
      scaleAnimationFrame = requestAnimationFrame(scaleAnimation);
      const adaptedScale = desiredScale / 5 + 0.3;

      if (orbCore && orbGlow) {
        orbCore.scale.lerp(
          new THREE.Vector3(adaptedScale, adaptedScale, adaptedScale),
          0.1
        );

        orbGlow.scale.lerp(
          new THREE.Vector3(
            adaptedScale * 1.1,
            adaptedScale * 1.1,
            adaptedScale * 1.1
          ),
          0.1
        );

        // Linearly interpolate the wave scale
        const currentCoreWaveScale = orbCore.material.uniforms.uWaveScale.value;
        const currentGlowWaveScale = orbGlow.material.uniforms.uWaveScale.value;
        const targetWaveScale = desiredScale / 7;

        orbCore.material.uniforms.uWaveScale.value =
          currentCoreWaveScale + (targetWaveScale - currentCoreWaveScale) * 0.2;
        orbGlow.material.uniforms.uWaveScale.value =
          currentGlowWaveScale + (targetWaveScale - currentGlowWaveScale) * 0.2;
      }
    };

    const animate = () => {
      if (!isInitialized) return;
      
      animFrame = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      // Default activity level
      let activity = 1.0;
      let scaledActivity = 0.5;

      // Get audio data if analyser is available
      if (analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        // Calculate average volume from frequency data
        const sum = dataArrayRef.current.reduce((a, b) => a + b, 0);
        const average = sum / dataArrayRef.current.length;

        // Scale the activity based on audio volume (with more dramatic effect)
        activity = 0.8 + (average / 128) * 1.2;
        scaledActivity = Math.min(1, average / 158);
      }
      desiredScale = scaledActivity;

      // Apply the activity level to the orb animations
      if (orbCore && orbGlow) {
        orbCore.material.uniforms.uTime.value = t * 1.5;
        orbGlow.material.uniforms.uTime.value = t * 1.5;

        orbCore.rotation.y = orbGlow.rotation.y = t * 0.2;
        orbCore.rotation.z = orbGlow.rotation.z = t * 0.1;

        renderer.render(scene, camera);
      }
    };

    initThreeOrb();
    if (isInitialized) {
      scaleAnimation();
      animate();
    }

    // Handle window resize
    const handleResize = () => {
      if (!isInitialized || !orbContainerRef.current || !renderer || !camera) return;

      const width = orbContainerRef.current.clientWidth;
      const height = orbContainerRef.current.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animFrame);
      cancelAnimationFrame(scaleAnimationFrame);
      if (renderer) {
        renderer.dispose();
        if (orbContainerRef.current) {
          orbContainerRef.current.innerHTML = "";
        }
      }
    };
  }, [visualizerMode]);

  // Microphone and canvas visualization effect
  useEffect(() => {
    if (visualizerMode === "bot" || isProcessing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;

    const setupMicrophone = async () => {
      if (!audioContextRef.current) return;

      try {
        // Resume audio context if necessary
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }

        // If we're muted, stop and cleanup any existing stream
        if (isMuted) {
          if (micSourceRef.current) {
            micSourceRef.current.disconnect();
            micSourceRef.current = null;
          }
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => {
              track.stop();
              track.enabled = false;
            });
            streamRef.current = null;
          }
          return;
        }

        // Only get new stream if we don't have one and we're not muted
        if (!streamRef.current && !isMuted) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          streamRef.current = stream;

          // Create new audio source
          const micSource = audioContextRef.current.createMediaStreamSource(stream);
          micSourceRef.current = micSource;
          micSource.connect(analyserRef.current);
        }
      } catch (err) {
        console.error("Error accessing microphone:", err);
      }
    };

    // Setup or cleanup microphone based on mute state
    setupMicrophone();

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      if (!analyserRef.current || !dataArrayRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const bufferLength = analyserRef.current.frequencyBinCount;

      // Calculate audio average for scaling
      const average =
        dataArrayRef.current.reduce((a, b) => a + b, 0) / bufferLength;
      const normalized = Math.min(1, average / 128); // Normalize between 0 and 1

      ctx.clearRect(0, 0, WIDTH, HEIGHT);

      if (visualizerMode === "mic") {
        // Draw a more dynamic visualization for mic input
        const centerX = WIDTH / 2;
        const centerY = HEIGHT / 2;

        // Base circle
        const baseRadius = 60;
        const pulseRadius = baseRadius + normalized * 40;

        // Main pulse circle
        ctx.beginPath();
        ctx.arc(centerX, centerY, pulseRadius, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(126, 70, 255, ${0.5 + normalized * 0.5})`;
        ctx.fill();

        // Glow effect
        const gradient = ctx.createRadialGradient(
          centerX,
          centerY,
          pulseRadius * 0.5,
          centerX,
          centerY,
          pulseRadius * 1.5
        );
        gradient.addColorStop(0, `rgba(160, 110, 255, ${normalized * 0.5})`);
        gradient.addColorStop(1, "rgba(126, 70, 255, 0)");

        ctx.beginPath();
        ctx.arc(centerX, centerY, pulseRadius * 1.5, 0, 2 * Math.PI);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Wave circles based on frequency bands
        for (let i = 0; i < 3; i++) {
          const bandStart = Math.floor(bufferLength / 5) * i;
          const bandEnd = Math.floor(bufferLength / 5) * (i + 1);
          let bandSum = 0;

          for (let j = bandStart; j < bandEnd; j++) {
            bandSum += dataArrayRef.current[j];
          }

          const bandAvg = bandSum / (bandEnd - bandStart);
          const bandRadius = baseRadius + 15 * i + (bandAvg / 255) * 20;

          ctx.beginPath();
          ctx.arc(centerX, centerY, bandRadius, 0, 2 * Math.PI);
          ctx.strokeStyle = `rgba(126, 70, 255, ${
            0.3 + (bandAvg / 255) * 0.7
          })`;
          ctx.lineWidth = 2 + (bandAvg / 255) * 3;
          ctx.stroke();
        }

        // Inner solid circle
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius, 0, 2 * Math.PI);
        ctx.fillStyle = "#7E46FF";
        ctx.fill();
      } else {
        // Simple visualization for processing state
        ctx.beginPath();
        const radius = Math.max(50, average);
        ctx.arc(WIDTH / 2, HEIGHT / 2, radius, 0, 2 * Math.PI);
        ctx.fillStyle = "#7E46FF";
        ctx.fill();
      }
    };

    draw();

    return () => {
      cancelAnimationFrame(animationRef.current);
      // Cleanup microphone when effect is cleaned up
      if (micSourceRef.current) {
        micSourceRef.current.disconnect();
        micSourceRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        streamRef.current = null;
      }
    };
  }, [visualizerMode, isProcessing, isMuted]);

  // Cleanup effect for when component is unmounted
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (micSourceRef.current) {
        micSourceRef.current.disconnect();
        micSourceRef.current = null;
      }

      if (externalSourceRef.current) {
        externalSourceRef.current.disconnect();
        externalSourceRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ position: "relative", width: 500, height: 500, background: "transparent" }}>
      {visualizerMode === "bot" && (
        <div ref={orbContainerRef} style={{ width: "100%", height: "100%", background: "transparent" }} />
      )}
      {visualizerMode !== "bot" && (
        <canvas 
          ref={canvasRef} 
          width={500} 
          height={500} 
          style={{ background: "transparent" }}
        />
      )}
      {!isProcessing && visualizerMode !== "bot" && !isMuted && (
        <MicIcon
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            fontSize: 40,
            opacity: 0.8,
            filter: "drop-shadow(0 0 8px rgba(255, 255, 255, 0.7))",
          }}
        />
      )}
      {!isProcessing && visualizerMode !== "bot" && isMuted && (
        <MicOffIcon
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            fontSize: 40,
            opacity: 0.8,
            filter: "drop-shadow(0 0 8px rgba(255, 255, 255, 0.7))",
          }}
        />
      )}
      {isProcessing && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            gap: 8,
          }}
        >
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#fff",
                animation: `blink 1.2s infinite`,
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      )}
      <style>
        {`
@keyframes blink {
  0%, 80%, 100% { opacity: 0; }
  40% { opacity: 1; }
}
`}
      </style>
    </div>
  );
};

export default VoiceVisualizer;
